import { DEFAULT_CHROME_CSS_STYLES, resolvedCardPlacement } from '../constants/bannerChrome';

// What the native app SDKs draw around a banner, resolved from the same
// `cssStyles` the web SDK reads — and resolved differently, which is the whole
// reason this exists. A port of sdk-react-native's `bannerCloseButton.ts` and
// `bannerChrome.ts`, which sdk-swift, sdk-kotlin and sdk-flutter all match.
//
// Two rules separate it from the web preview:
//   - Six close-button keys are web only (`closeButtonSymbol`,
//     `closeButtonPadding`, `closeButtonFontWeight`, `closeButtonLineHeight`,
//     `closeButtonTopPosition`, `closeButtonRightPosition`), as are the Unlayer
//     design's own `popupCloseButton_*` colours. None is read here, so the
//     admin's default `closeButtonTopPosition: '-14'` cannot hang the ✕ outside
//     the card the way it does on the web.
//   - A CARD key equal to its own default reads as unset rather than as a
//     value, so an unstyled block gets the app's own card instead of the
//     admin's seed values. The close-button keys are the other way round: every
//     one of them resolves to a concrete colour or number.
//
// Every authored value reaches the markup below through one of the parsers
// here, so nothing that is not a colour, a length or a validated url can end up
// inside a style attribute.

// Hex is read in CSS order — alpha LAST — so `#ffff00ff` is yellow and
// `#ff000080` is half-transparent red. A named colour other than `transparent`
// is outside the set the admin's fields accept and falls back to the default.
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/;
const RGB = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
const RGBA = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0|1|1\.0+|0?\.\d+)\s*\)$/;

// A length in CSS pixels. The unit is optional: the API serves `closeFontSize`
// and the radii bare, while a border shorthand and a design's own dimensions
// carry `px`.
const LENGTH = /^(\d+(?:\.\d+)?)(?:px)?$/;

// A card size or offset: the closed set the API accepts, so no `vw`, `rem` or
// `calc()` can arrive.
const SIZED = /^(-?\d+(?:\.\d+)?)(px|%)$/;

// The border shorthand's tokens, keeping a colour function whole — splitting on
// whitespace alone would cut `rgb(1, 2, 3)` into three tokens, none of them a
// colour.
const BORDER_TOKENS = /[a-z]+\([^)]*\)|\S+/g;

const GLYPH_MIN = 8;
const GLYPH_MAX = 26;
const GLYPH_DEFAULT = 14;
const CLOSE_DEFAULT_COLOR = '#000';
const CLOSE_DEFAULT_BACKGROUND_COLOR = '#fff';
const CLOSE_DEFAULT_BORDER_RADIUS = 20;

const CARD_CLOSE_INSET = 8;
const BAR_CLOSE_INSET = 4;

// A popup is capped this far short of the viewport on every side, and never
// narrower than this.
const POPUP_MARGIN = 32;
const POPUP_MIN_WIDTH = 120;
const POPUP_DEFAULT_WIDTH = 600;
const POPUP_DEFAULT_RADIUS = 10;
const DEFAULT_OVERLAY = 'rgba(0,0,0,0.5)';
const DEFAULT_CARD_BACKGROUND = '#fff';
const FLYOUT_WIDTH = '80%';

/** A close-button value an author set, trimmed and lower-cased, or null. */
const closeValue = (cssStyles, key) => {
  const value = String(cssStyles?.[key] ?? '')
    .trim()
    .toLowerCase();
  return value === '' ? null : value;
};

// A card value an author set, or null when it resolves to its own default.
// Compared lower-cased: the defaults are shared with the API and the SDKs, so a
// case near-miss must not read as author intent.
const cardValue = (cssStyles, key) => {
  const value = String(cssStyles?.[key] ?? DEFAULT_CHROME_CSS_STYLES[key]).trim();
  return value.toLowerCase() === DEFAULT_CHROME_CSS_STYLES[key].toLowerCase() ? null : value;
};

export const parseChromeColor = (value) => {
  if (!value) {
    return null;
  }

  const text = String(value).trim().toLowerCase();

  if (text === 'transparent' || HEX.test(text)) {
    return text;
  }

  const rgb = RGB.exec(text);
  if (rgb) {
    return `rgb(${rgb[1]},${rgb[2]},${rgb[3]})`;
  }

  const rgba = RGBA.exec(text);
  return rgba ? `rgba(${rgba[1]},${rgba[2]},${rgba[3]},${rgba[4]})` : null;
};

const parsePxLength = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const match = LENGTH.exec(String(value).trim().toLowerCase());
  return match ? parseFloat(match[1]) : null;
};

// The CSS `border` shorthand reduced to what a native control draws: the first
// length token is the width (1 when the shorthand names only a colour) and the
// first token the colour parser accepts is the colour. The style token is
// dropped — the stroke is always solid. Without a colour there is no border,
// which is how `''`, `none` and `0` all resolve.
export const parseCloseButtonBorder = (value) => {
  if (!value) {
    return null;
  }

  let width = null;
  let color = null;

  for (const token of String(value).trim().toLowerCase().match(BORDER_TOKENS) ?? []) {
    width ??= parsePxLength(token);
    color ??= parseChromeColor(token);
  }

  return color === null ? null : { width: width ?? 1, color };
};

export const resolveAppCloseButton = (cssStyles) => {
  const fontSize = Math.min(
    Math.max(parsePxLength(closeValue(cssStyles, 'closeFontSize')) ?? GLYPH_DEFAULT, GLYPH_MIN),
    GLYPH_MAX
  );
  // The default 14 keeps the 32 the apps have always drawn, and the clamp above
  // caps the side at 44 — never above the smaller of the two tap targets.
  const size = Math.max(32, fontSize + 18);

  return {
    color: parseChromeColor(closeValue(cssStyles, 'closeButtonColor')) ?? CLOSE_DEFAULT_COLOR,
    backgroundColor:
      parseChromeColor(closeValue(cssStyles, 'closeButtonBackgroundColor')) ?? CLOSE_DEFAULT_BACKGROUND_COLOR,
    border: parseCloseButtonBorder(closeValue(cssStyles, 'closeButtonBorder')),
    fontSize,
    size,
    // Capped at half the side, which is what keeps the admin's 20 a circle at
    // 32 rather than a radius larger than the button.
    borderRadius: Math.min(
      parsePxLength(closeValue(cssStyles, 'closeButtonBorderRadius')) ?? CLOSE_DEFAULT_BORDER_RADIUS,
      size / 2
    ),
  };
};

// A card size, as a CSS length. Neither negative nor unitless, so a bare `20`
// is never a width. Rejected on the sign in the text rather than on the parsed
// number, because `-0 < 0` is false.
const parseCardSize = (value) => {
  const match = value === null ? null : SIZED.exec(value.toLowerCase());
  return !match || match[1].startsWith('-') ? null : `${parseFloat(match[1])}${match[2]}`;
};

// An offset in pixels: how far the card is translated from wherever it was
// placed. A percentage resolves against the viewport axis it applies to, and a
// bare `0` is legal where a size is not.
const parseCardOffset = (value, extent) => {
  if (value === null) {
    return null;
  }

  const text = value.toLowerCase();
  if (/^-?0$/.test(text)) {
    return 0;
  }

  const match = SIZED.exec(text);
  if (!match) {
    return null;
  }

  const number = parseFloat(match[1]);
  return match[2] === '%' ? (extent * number) / 100 : number;
};

// A positive offset is a gap from the edge the card is anchored to, so the end
// anchors — bottom and right — negate it. That is the contract every SDK
// implements (magellan-api `constants/banner-chrome-css-styles.md`).
const offsetTranslation = (offset, anchor) =>
  anchor === 'bottom' || anchor === 'right' ? -(offset ?? 0) : (offset ?? 0);

// Only an absolute http(s) url, and only one that cannot escape the `url()` it
// is written into.
export const parseBackgroundImageUrl = (backgroundImage) => {
  if (!backgroundImage?.url) {
    return null;
  }

  let url;
  try {
    url = new URL(backgroundImage.url);
  } catch {
    return null;
  }

  return /^https?:$/.test(url.protocol) && !/["'()\\\s]/.test(url.href) ? url.href : null;
};

/**
 * The card the app SDKs draw, for one block. `designValues` is the Unlayer
 * design's `body.values`, which a popup falls back to for its width, radius,
 * background and overlay, and a flyout for its background colour and image;
 * `viewport` is the phone the preview renders against.
 */
export const resolveAppCard = ({ displayType, displayPosition, cssStyles, designValues, viewport }) => {
  const { vertical, horizontal } = resolvedCardPlacement({ displayType, displayPosition, cssStyles });
  const width = parseCardSize(cardValue(cssStyles, 'cardWidth'));
  const height = parseCardSize(cardValue(cssStyles, 'cardHeight'));
  const radius = parsePxLength(cardValue(cssStyles, 'cardBorderRadius'));
  const background = parseChromeColor(cardValue(cssStyles, 'cardBackgroundColor'));
  const offsetX = parseCardOffset(cardValue(cssStyles, 'cardOffsetHorizontal'), viewport.width);
  const offsetY = parseCardOffset(cardValue(cssStyles, 'cardOffsetVertical'), viewport.height);

  const card = {
    vertical,
    horizontal,
    height,
    minWidth: null,
    maxWidth: null,
    maxHeight: null,
    borderRadius: radius ?? 0,
    backgroundColor: background ?? DEFAULT_CARD_BACKGROUND,
    backgroundImage: null,
    // A flyout with no height of its own is a panel pinned top to bottom; the
    // other two are placed by the layer around them.
    stretchVertically: displayType === 'flyout' && height === null,
    overlayColor:
      displayType === 'bar'
        ? null
        : (parseChromeColor(cardValue(cssStyles, 'overlayColor')) ??
          parseChromeColor(designValues?.popupOverlay_backgroundColor) ??
          DEFAULT_OVERLAY),
    offsetX: horizontal === null ? 0 : offsetTranslation(offsetX, horizontal),
    offsetY: offsetTranslation(offsetY, vertical),
  };

  if (displayType === 'flyout') {
    return {
      ...card,
      width: width ?? FLYOUT_WIDTH,
      backgroundColor: background ?? parseChromeColor(designValues?.backgroundColor) ?? DEFAULT_CARD_BACKGROUND,
      backgroundImage: parseBackgroundImageUrl(designValues?.backgroundImage),
    };
  }

  // A bar spans the viewport: width, horizontal position and horizontal offset
  // have no meaning for the format, and the admin strips them on save
  // (`INAPPLICABLE_CSS_STYLE_KEYS`).
  if (displayType === 'bar') {
    return { ...card, width: null };
  }

  return {
    ...card,
    width: width ?? `${parsePxLength(designValues?.popupWidth) ?? POPUP_DEFAULT_WIDTH}px`,
    minWidth: width === null ? POPUP_MIN_WIDTH : null,
    maxWidth: Math.max(viewport.width - POPUP_MARGIN, POPUP_MIN_WIDTH),
    maxHeight: Math.max(viewport.height - POPUP_MARGIN, POPUP_MIN_WIDTH),
    borderRadius: radius ?? parsePxLength(designValues?.borderRadius) ?? POPUP_DEFAULT_RADIUS,
    backgroundColor: background ?? parseChromeColor(designValues?.popupBackgroundColor) ?? DEFAULT_CARD_BACKGROUND,
    backgroundImage: parseBackgroundImageUrl(designValues?.backgroundImage),
  };
};

const ALIGN = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

const px = (value) => (value === null ? null : `${value}px`);

// Shared with `appDesignMarkup`, which writes the banner's own content inside
// this chrome and so emits the same kind of inline declarations.
export const inlineStyle = (declarations) =>
  Object.entries(declarations)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([property, value]) => `${property}:${value}`)
    .join(';');

// The ✕ itself: two strokes crossed in a box the authored `closeFontSize`
// sizes. Drawn rather than typed, which is what leaves `closeButtonSymbol`
// nothing to say.
const glyphMarkup = (button) => {
  const stroke = (angle) =>
    `<span style="${inlineStyle({
      position: 'absolute',
      top: '50%',
      left: 0,
      width: px(button.fontSize),
      height: '2px',
      'margin-top': '-1px',
      'background-color': button.color,
      transform: `rotate(${angle}deg)`,
    })}"></span>`;

  const box = inlineStyle({
    position: 'relative',
    display: 'block',
    width: px(button.fontSize),
    height: px(button.fontSize),
  });

  return `<span style="${box}">${stroke(45)}${stroke(-45)}</span>`;
};

const closeControlMarkup = (button, placement) =>
  `<div style="${inlineStyle({
    position: 'absolute',
    'z-index': 1,
    ...placement,
    'box-sizing': 'border-box',
    width: px(button.size),
    height: px(button.size),
    'border-radius': px(button.borderRadius),
    'background-color': button.backgroundColor,
    border: button.border === null ? null : `${button.border.width}px solid ${button.border.color}`,
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
  })}">${glyphMarkup(button)}</div>`;

/**
 * The whole app render for one banner block: the layer the card is placed in,
 * the card, the close control and the banner's own markup inside them.
 */
export const appBannerChromeMarkup = ({ html, displayType, displayPosition, cssStyles, designValues, viewport }) => {
  const card = resolveAppCard({ displayType, displayPosition, cssStyles, designValues, viewport });
  const button = resolveAppCloseButton(cssStyles);
  const isBar = displayType === 'bar';

  const layer = inlineStyle({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    'align-items': card.stretchVertically ? 'stretch' : ALIGN[card.vertical],
    'justify-content': JUSTIFY[card.horizontal ?? 'center'],
    'background-color': card.overlayColor,
  });

  const cardStyle = inlineStyle({
    position: 'relative',
    'box-sizing': 'border-box',
    display: 'flex',
    'flex-direction': 'column',
    overflow: 'hidden',
    width: card.width ?? '100%',
    'min-width': px(card.minWidth),
    'max-width': px(card.maxWidth),
    height: card.height,
    'max-height': px(card.maxHeight),
    'border-radius': px(card.borderRadius),
    'background-color': card.backgroundColor,
    'background-image': card.backgroundImage === null ? null : `url('${card.backgroundImage.replaceAll('&', '&amp;')}')`,
    'background-size': card.backgroundImage === null ? null : 'cover',
    'background-position': card.backgroundImage === null ? null : 'center',
    'box-shadow': displayType === 'popup' ? '0 8px 24px rgba(0,0,0,0.25)' : null,
    transform: card.offsetX || card.offsetY ? `translate(${card.offsetX}px,${card.offsetY}px)` : null,
  });

  // The band/gutter the control sits in is derived from its own size, so the
  // two stay in step whatever glyph size resolves — a popup/flyout band is the
  // control's inset on both sides of its size, same for a bar's gutter.
  const band = CARD_CLOSE_INSET * 2 + button.size;
  const gutter = BAR_CLOSE_INSET * 2 + button.size;

  // A bar pads its content clear of the gutter the control sits in; a popup or
  // a flyout reserves the band above it and scrolls whatever does not fit.
  const contentStyle = isBar
    ? inlineStyle({ padding: `12px ${gutter}px 12px 16px` })
    : inlineStyle({ flex: '1 1 auto', 'min-height': 0, overflow: 'auto', 'padding-top': px(band) });

  // Always inside the card. A flyout's control sits on the panel's inner edge,
  // which is the left edge of a right-hand flyout; a bar's at its top-right
  // corner, where every app SDK anchors it.
  const placement = isBar
    ? { top: px(BAR_CLOSE_INSET), right: px(BAR_CLOSE_INSET) }
    : displayType === 'flyout' && card.horizontal === 'right'
      ? { top: px(CARD_CLOSE_INSET), left: px(CARD_CLOSE_INSET) }
      : { top: px(CARD_CLOSE_INSET), right: px(CARD_CLOSE_INSET) };

  // The control is written before the content: the content is pasted into the
  // same parse stream, so a stray close tag, an unclosed comment or an unclosed
  // <style> in a hand-written banner can only swallow what follows it.
  return (
    `<div style="${layer}"><div style="${cardStyle}">` +
    closeControlMarkup(button, placement) +
    `<div style="${contentStyle}">${html ?? ''}</div></div></div>`
  );
};
